import React, { useCallback, useEffect, useRef, useState } from "react";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import {
  Upload, Users, UserCog, Trash2, Loader2, AlertCircle, CheckCircle2, Download, RefreshCw, ShieldCheck,
} from "lucide-react";
import api, { formatError } from "../lib/api";

function MappingUploadCard({ id, title, subtitle, endpoint, expected, template, accent, icon: Icon, onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  async function upload(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setError(""); setResult(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      onDone && onDone();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function downloadTemplate() {
    const blob = new Blob([template.join(",") + "\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${id}-template.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <div className="flex items-start gap-4">
        <div className={`h-11 w-11 rounded-md grid place-items-center bg-${accent} text-white shrink-0`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-medium text-slate-900">{title}</h3>
          <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">{subtitle}</p>
          <div className="mt-2 flex flex-wrap gap-1 items-center">
            <span className="small-caps text-slate-400">Expected columns:</span>
            {expected.map((c) => (
              <code key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">{c}</code>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <input type="file" accept=".xlsx,.xls,.csv" ref={inputRef} onChange={upload} className="hidden" data-testid={`fl-input-${id}`} />
            <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid={`fl-upload-${id}`}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {busy ? "Uploading…" : "Choose file (.xlsx / .csv)"}
            </button>
            <button onClick={downloadTemplate} className="text-[12px] text-teal-700 hover:underline inline-flex items-center gap-1" data-testid={`fl-template-${id}`}>
              <Download size={13} /> Download template
            </button>
          </div>
          {error && (
            <div className="mt-3 tag tag-rose flex items-start gap-2"><AlertCircle size={13} className="mt-0.5" /> <span data-testid={`fl-error-${id}`}>{error}</span></div>
          )}
          {result && (
            <div className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200" data-testid={`fl-result-${id}`}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(result).filter(([k]) => k !== "ok" && k !== "unknown_outlet_sample").map(([k, v]) => (
                  <div key={k} className="text-[12px]">
                    <div className="small-caps text-slate-400">{k.replace(/_/g, " ")}</div>
                    <div className="serif-display text-xl text-slate-900 mt-0.5 numerals">{String(v)}</div>
                  </div>
                ))}
              </div>
              {result.unknown_outlet_sample?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200 text-[11px] text-amber-700">
                  <Tag tone="amber">Not found in Physio Master</Tag> {result.unknown_outlet_sample.join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function FieldLogins() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/field-logins");
      setData(data);
    } catch (e) { setError(formatError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function remove(mobile, name) {
    if (!window.confirm(`Delete the login for ${name || mobile} (${mobile})?\n\nThey will no longer be able to sign in. Physio data is NOT affected.`)) return;
    setDeleting(mobile);
    try {
      await api.delete(`/admin/field-logins/${mobile}`);
      await load();
    } catch (e) { alert(formatError(e)); }
    finally { setDeleting(""); }
  }

  const items = data?.items || [];

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Administration"
        title="DSE Mapping & Logins"
        subtitle="Upload which physios each DSE (and Zonal Manager) is responsible for, so every field login sees only their own physios. Manage and remove logins from here."
        testId="field-logins-page"
      />

      <div className="space-y-4">
        <MappingUploadCard
          id="dse-mapping"
          title="DSE → Physio Mapping"
          subtitle="One physio Outlet Id per row (the same Contact/mobile can appear on many rows). Each DSE login then sees ONLY the physios mapped to their number. Re-uploading replaces that number's list. Accepts .xlsx or .csv."
          endpoint="/admin/imports/dse-mapping"
          expected={["Contact", "Role of Contact", "Mapped Physio Outlet ID"]}
          template={["Contact", "Role of Contact", "Mapped Physio Outlet ID"]}
          accent="brand-navy"
          icon={Users}
          onDone={load}
        />
        <MappingUploadCard
          id="zm-mapping"
          title="Zonal Manager → DSE Mapping"
          subtitle="One DSE Contact per row against their Zonal Manager Contact. The ZM login then sees the combined physios of all their DSEs. Re-uploading replaces that ZM's DSE list. Accepts .xlsx or .csv."
          endpoint="/admin/imports/zm-mapping"
          expected={["Zonal Manager Contact", "DSE Contact", "Zonal Manager Name"]}
          template={["Zonal Manager Contact", "DSE Contact", "Zonal Manager Name"]}
          accent="teal-600"
          icon={UserCog}
          onDone={load}
        />
      </div>

      <Card
        title="Configured Field Logins"
        subtitle="Every DSE / Zonal Manager who can sign in via the Field — Mobile login. Uploaded mappings are the source of truth for what each login sees; whitelist rows have no explicit physio mapping yet."
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-[12px] text-slate-500">
            {data ? <>Showing <strong>{fmt(data.count)}</strong> logins · <strong>{fmt(data.uploaded)}</strong> uploaded mappings</> : "…"}
          </div>
          <button onClick={load} className="btn-secondary !py-1.5 inline-flex items-center gap-1.5 text-[12px]" data-testid="fl-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {error && <div className="tag tag-rose inline-flex items-center gap-1 mb-3"><AlertCircle size={13} /> {error}</div>}

        {loading ? (
          <div className="py-12 text-center text-slate-400 inline-flex items-center gap-2 justify-center w-full"><Loader2 size={16} className="animate-spin" /> Loading logins…</div>
        ) : (
          <div className="overflow-auto rounded-lg border border-slate-100" data-testid="fl-table">
            <table className="w-full text-[12.5px]">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Name</th>
                  <th className="text-left font-medium px-3 py-2">Mobile</th>
                  <th className="text-left font-medium px-3 py-2">Role</th>
                  <th className="text-right font-medium px-3 py-2">Physios</th>
                  <th className="text-right font-medium px-3 py-2">DSEs</th>
                  <th className="text-left font-medium px-3 py-2">Source</th>
                  <th className="text-right font-medium px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No field logins configured yet — upload a DSE mapping above.</td></tr>
                )}
                {items.map((it, i) => (
                  <tr key={it.mobile} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fl-row-${it.mobile}`}>
                    <td className="px-3 py-2 font-medium text-slate-700">{it.name && it.name !== "—" ? it.name : it.mobile}</td>
                    <td className="px-3 py-2 text-slate-600 font-mono">{it.mobile}</td>
                    <td className="px-3 py-2">
                      <Tag tone={it.role === "zonal_manager" ? "amber" : "teal"}>{it.role_label}</Tag>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-800">{it.physio_count == null ? "—" : fmt(it.physio_count)}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{it.dse_count == null ? "—" : fmt(it.dse_count)}</td>
                    <td className="px-3 py-2">
                      {it.source === "uploaded"
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck size={12} /> Uploaded</span>
                        : <span className="text-slate-400">Whitelist</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {it.source === "uploaded" ? (
                        <button
                          onClick={() => remove(it.mobile, it.name)}
                          disabled={deleting === it.mobile}
                          className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 disabled:opacity-50 text-[12px]"
                          data-testid={`fl-delete-${it.mobile}`}
                        >
                          {deleting === it.mobile ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete
                        </button>
                      ) : <span className="text-slate-300 text-[11px]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
